import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { leadService, LeadCreate, LeadUpdate } from '../../services/leadService';
import { userService } from '../../services/userService';

const LeadForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const { data: leadData } = useQuery(
    ['lead', id],
    () => leadService.getById(Number(id!)),
    { enabled: isEdit }
  );

  const { data: usersData } = useQuery(
    'users',
    () => userService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<LeadCreate | LeadUpdate>({
    defaultValues: isEdit && leadData?.data.lead ? {
      nombre: leadData.data.lead.nombre,
      email: leadData.data.lead.email,
      telefono: leadData.data.lead.telefono,
      empresa: leadData.data.lead.empresa,
      cargo: leadData.data.lead.cargo,
      direccion: leadData.data.lead.direccion,
      origen: leadData.data.lead.origen,
      estado: leadData.data.lead.estado,
      probabilidad: leadData.data.lead.probabilidad,
      valorEstimado: leadData.data.lead.valorEstimado,
      fechaCierreEstimada: leadData.data.lead.fechaCierreEstimada,
      usuarioAsignadoId: leadData.data.lead.usuarioAsignadoId,
      notas: leadData.data.lead.notas
    } : {
      origen: 'manual',
      estado: 'nuevo',
      probabilidad: 0,
      valorEstimado: 0
    }
  });

  const createMutation = useMutation(leadService.create, {
    onSuccess: (data) => {
      toast.success('Lead creado correctamente');
      queryClient.invalidateQueries('leads');
      navigate(`/crm/leads/${data.data.lead.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear lead');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: LeadUpdate }) => leadService.update(id, data),
    {
      onSuccess: () => {
        toast.success('Lead actualizado correctamente');
        queryClient.invalidateQueries('leads');
        navigate(`/crm/leads/${id}`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar lead');
      }
    }
  );

  const onSubmit = (data: any) => {
    if (isEdit) {
      updateMutation.mutate({ id: Number(id!), data });
    } else {
      createMutation.mutate(data);
    }
  };

  const origenes = ['manual', 'web', 'referido', 'evento', 'redes_sociales', 'email', 'telefono', 'otro'];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate(isEdit ? `/crm/leads/${id}` : '/crm/leads')}
            className="text-gray-600 hover:text-gray-800 mb-2"
          >
            ← Volver
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            {isEdit ? 'Editar Lead' : 'Nuevo Lead'}
          </h1>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información básica */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información del Contacto</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Contacto *
                </label>
                <input
                  {...register('nombre', { required: 'Nombre del contacto es requerido' })}
                  placeholder="Ej: Juan Pérez"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.nombre ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.nombre && (
                  <p className="text-red-500 text-sm mt-1">{errors.nombre.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', {
                    validate: (value, formValues) => {
                      if (!value && !formValues.telefono) {
                        return 'Debe proporcionar al menos un email o teléfono';
                      }
                      return true;
                    }
                  })}
                  placeholder="contacto@empresa.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.email ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  {...register('telefono', {
                    validate: (value, formValues) => {
                      if (!value && !formValues.email) {
                        return 'Debe proporcionar al menos un email o teléfono';
                      }
                      if (value && !/^\d{10}$/.test(value.replace(/\D/g, ''))) {
                        return 'El teléfono debe tener exactamente 10 dígitos';
                      }
                      return true;
                    }
                  })}
                  placeholder="5551234567"
                  maxLength={10}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.telefono ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.telefono && (
                  <p className="text-red-500 text-sm mt-1">{errors.telefono.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Al menos email o teléfono es requerido</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <input
                  {...register('empresa')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cargo
                </label>
                <input
                  {...register('cargo')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Origen
                </label>
                <select
                  {...register('origen')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {origenes.map((origen) => (
                    <option key={origen} value={origen}>
                      {origen.charAt(0).toUpperCase() + origen.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <textarea
                  {...register('direccion')}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Información de venta */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información de Venta</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  {...register('estado')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="nuevo">Nuevo</option>
                  <option value="contactado">Contactado</option>
                  <option value="calificado">Calificado</option>
                  <option value="descalificado">Descalificado</option>
                  <option value="convertido">Convertido</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Probabilidad (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  {...register('probabilidad', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Estimado
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  {...register('valorEstimado', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de Cierre Estimada
                </label>
                <input
                  type="date"
                  {...register('fechaCierreEstimada')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asignado a
                </label>
                <select
                  {...register('usuarioAsignadoId', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Sin asignar</option>
                  {usersData?.data.users?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              {...register('notas')}
              rows={4}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate(isEdit ? `/crm/leads/${id}` : '/crm/leads')}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Guardando...'
                : isEdit
                ? 'Actualizar'
                : 'Crear Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadForm;

